/**
 * CategoryIcon - Render category icons from name strings (WP-CAT-13)
 *
 * Maps icon name strings (from database) to inline SVG components.
 * Provides backward compatibility for emoji icons.
 *
 * Features:
 * - Maps 27 icon names to inline SVG components
 * - Backward compatibility: emoji strings render as-is
 * - Falls back to circle icon for unknown/null/undefined names
 * - Supports size prop ('sm' | 'md' | 'lg')
 * - Supports className and color props
 *
 * Icon names from: services/core/alembic/versions/001_initial.py:647-700
 */
import type { FC, CSSProperties } from 'react';
import { cn } from '@/utils/cn';
import { CATEGORY_ICON_MAP, CircleIcon } from './categoryIconSvgs.js';

// ============================================================================
// Types
// ============================================================================

export type CategoryIconSize = 'sm' | 'md' | 'lg';

/**
 * Valid icon names for categories.
 * Matches database seed icons + additional user icons.
 */
export type CategoryIconName =
  // Database seed icons (15)
  | 'trending-up'
  | 'trending-down'
  | 'repeat'
  | 'briefcase'
  | 'bar-chart'
  | 'laptop'
  | 'home'
  | 'plus-circle'
  | 'car'
  | 'utensils'
  | 'heart'
  | 'music'
  | 'shopping-bag'
  | 'more-horizontal'
  | 'credit-card'
  // Additional user icons (12)
  | 'coffee'
  | 'plane'
  | 'gift'
  | 'book'
  | 'phone'
  | 'wifi'
  | 'scissors'
  | 'tag'
  | 'dollar-sign'
  | 'building'
  | 'fuel'
  | 'circle';

export interface CategoryIconProps {
  /** Icon name string or emoji */
  name?: string | null;
  /** Size variant */
  size?: CategoryIconSize;
  /** Additional CSS classes */
  className?: string;
  /** Optional color (hex) - overrides currentColor */
  color?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Size class mappings for icons (matches existing project patterns).
 * Pattern observed in: notificationIcons.tsx, AccountTypeIcon.tsx
 */
const SIZE_CLASSES: Record<CategoryIconSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

/**
 * Size class mappings for emoji fallback spans.
 */
const EMOJI_SIZE_CLASSES: Record<CategoryIconSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

/**
 * Array of all supported icon names for use in IconPicker.
 * Excludes 'circle' as it's the fallback icon.
 */
export const SUPPORTED_ICON_NAMES: CategoryIconName[] = [
  // Database seed icons (15)
  'trending-up',
  'trending-down',
  'repeat',
  'briefcase',
  'bar-chart',
  'laptop',
  'home',
  'plus-circle',
  'car',
  'utensils',
  'heart',
  'music',
  'shopping-bag',
  'more-horizontal',
  'credit-card',
  // Additional user icons (11 - excluding circle fallback)
  'coffee',
  'plane',
  'gift',
  'book',
  'phone',
  'wifi',
  'scissors',
  'tag',
  'dollar-sign',
  'building',
  'fuel',
];

/**
 * Display names for icons (for accessibility and UI display).
 */
export const ICON_DISPLAY_NAMES: Record<CategoryIconName, string> = {
  'trending-up': 'Trending Up',
  'trending-down': 'Trending Down',
  'repeat': 'Repeat',
  'briefcase': 'Briefcase',
  'bar-chart': 'Bar Chart',
  'laptop': 'Laptop',
  'home': 'Home',
  'plus-circle': 'Plus Circle',
  'car': 'Car',
  'utensils': 'Utensils',
  'heart': 'Heart',
  'music': 'Music',
  'shopping-bag': 'Shopping Bag',
  'more-horizontal': 'More',
  'credit-card': 'Credit Card',
  'coffee': 'Coffee',
  'plane': 'Plane',
  'gift': 'Gift',
  'book': 'Book',
  'phone': 'Phone',
  'wifi': 'WiFi',
  'scissors': 'Scissors',
  'tag': 'Tag',
  'dollar-sign': 'Dollar',
  'building': 'Building',
  'fuel': 'Fuel',
  'circle': 'Circle',
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Check if a string is an emoji (Unicode symbol range).
 *
 * Emoji ranges:
 * - Miscellaneous Symbols: U+2600 - U+26FF
 * - Dingbats: U+2700 - U+27BF
 * - Emoticons: U+1F600 - U+1F64F
 * - Misc Symbols and Pictographs: U+1F300 - U+1F5FF
 * - Supplemental Symbols: U+1F900 - U+1F9FF
 * - Transport and Map: U+1F680 - U+1F6FF
 *
 * @param str - String to check
 * @returns True if string starts with an emoji character
 */
function isEmoji(str: string): boolean {
  if (!str || str.length === 0) return false;
  const codePoint = str.codePointAt(0) || 0;

  // Comprehensive emoji detection
  // Check various emoji ranges
  return (
    // Miscellaneous Symbols
    (codePoint >= 0x2600 && codePoint <= 0x26FF) ||
    // Dingbats
    (codePoint >= 0x2700 && codePoint <= 0x27BF) ||
    // CJK Symbols
    (codePoint >= 0x2300 && codePoint <= 0x23FF) ||
    // Supplemental Arrows
    (codePoint >= 0x2B00 && codePoint <= 0x2BFF) ||
    // Emoticons, Transport, Symbols, Pictographs (U+1F000+)
    codePoint >= 0x1F000
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * CategoryIcon component - renders category icons from name strings.
 *
 * Handles three cases:
 * 1. Known icon name -> renders SVG icon
 * 2. Emoji string -> renders emoji as-is (backward compatibility)
 * 3. Unknown/null/undefined -> renders fallback circle icon
 */
const CategoryIcon: FC<CategoryIconProps> = ({
  name,
  size = 'md',
  className,
  color,
}) => {
  // Handle null/undefined/empty string - render fallback
  if (!name || name.trim() === '') {
    return (
      <CircleIcon
        className={cn(SIZE_CLASSES[size], 'text-gray-400', className)}
      />
    );
  }

  // Handle emoji (backward compatibility for user categories)
  if (isEmoji(name)) {
    const style: CSSProperties = color ? { color } : {};
    return (
      <span
        className={cn(EMOJI_SIZE_CLASSES[size], className)}
        style={style}
        aria-hidden="true"
      >
        {name}
      </span>
    );
  }

  // Handle known icon name
  const IconComponent = CATEGORY_ICON_MAP[name];
  if (IconComponent) {
    const iconStyle: CSSProperties | undefined = color ? { color } : undefined;
    return (
      <IconComponent
        className={cn(SIZE_CLASSES[size], className)}
        style={iconStyle}
      />
    );
  }

  // Unknown icon name - render fallback
  return (
    <CircleIcon
      className={cn(SIZE_CLASSES[size], 'text-gray-400', className)}
    />
  );
};

export default CategoryIcon;
